import PropTypes from 'prop-types'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import Relationships from 'shared_src/dictionaries/demographics/Insurance'

import {
  CancelButton,
  DateInput,
  MaskedTextInput,
  RadioGroupInput,
  SaveButton,
  SelectInput,
  TextInput,
  removeBlanks,
  removeBlanksKeepArray
} from '../../../../../shared/FormBuilder'
import { Paper } from '../../../../../shared/NarusMaterial'
import { SystemManaged } from '../../../../../shared/SystemManaged'
import { DiscardDialogContext } from '../../../../../../lib/global-discard-dialog'
import { useAppSelector } from "../../../../../../store";

const isPlainObject = value => (
    Object.prototype.toString.call(value) === '[object Object]'
)

const cloneDeep = value => {
  return JSON.parse(JSON.stringify(value))
}

const pathToArray = path => {
  if (!path) {
    return []
  }

  if (Array.isArray(path)) {
    return path
  }

  return `${path}`
      .replace(/\[(\d+)\]/g, '.$1')
      .split('.')
      .filter(Boolean)
}

const getIn = (object, path, defaultValue) => {
  const pathArray = pathToArray(path)

  if (!pathArray.length) {
    return object ?? defaultValue
  }

  let current = object

  for (const segment of pathArray) {
    if (current == null || typeof current !== 'object') {
      return defaultValue
    }

    current = current[segment]
  }

  return current === undefined ? defaultValue : current
}

const setIn = (object, path, value) => {
  const pathArray = pathToArray(path)

  if (!pathArray.length) {
    return value
  }

  let current = object

  pathArray.forEach((segment, index) => {
    if (index === pathArray.length - 1) {
      current[segment] = value
      return
    }

    if (
        current[segment] == null ||
        typeof current[segment] !== 'object'
    ) {
      const nextSegment = pathArray[index + 1]
      const shouldBeArray = Number.isInteger(Number(nextSegment))
      current[segment] = shouldBeArray ? [] : {}
    }

    current = current[segment]
  })

  return object
}

const deepEqual = (a, b) => {
  if (a === b) {
    return true
  }

  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime()
  }

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) {
      return false
    }

    for (let i = 0; i < a.length; i += 1) {
      if (!deepEqual(a[i], b[i])) {
        return false
      }
    }

    return true
  }

  if (isPlainObject(a) && isPlainObject(b)) {
    const keysA = Object.keys(a)
    const keysB = Object.keys(b)

    if (keysA.length !== keysB.length) {
      return false
    }

    for (const key of keysA) {
      if (!Object.prototype.hasOwnProperty.call(b, key)) {
        return false
      }

      if (!deepEqual(a[key], b[key])) {
        return false
      }
    }

    return true
  }

  if (a == null || b == null) {
    return false
  }

  if (typeof a === 'object' || typeof b === 'object') {
    return false
  }

  return a === b
}

const compact = array => array.filter(Boolean)

class LegacyFormProvider extends React.Component {
  getChildContext() {
    return {
      form: this.props.form,
      parentInputPath: '',
      formKey: this.props.formKey
    }
  }


  render() {
    return this.props.children
  }
}

LegacyFormProvider.childContextTypes = {
  form: PropTypes.instanceOf(Object),
  parentInputPath: PropTypes.string,
  formKey: PropTypes.string
}

function InsuranceForm(props) {
  const profile = useAppSelector(state => state.auth.profile)
  const role = profile?.app_metadata.roles?.meta?.role

  console.log({ profile, role })

  const canEditSystemManaged = ['administrator', 'executive_director'].includes(role)

  const {
    value: propValue,
    initialValues: propInitialValues,
    onSubmit,
    onCancel,
    onChange,
    noGlobal,
    formKey
  } = props

  const discardDialog = useContext(DiscardDialogContext)

  const initialValueRef = useRef(
      cloneDeep(propValue ?? propInitialValues ?? {})
  )

  const [formState, setFormState] = useState(() => ({
    value: cloneDeep(initialValueRef.current),
    initialValues: cloneDeep(initialValueRef.current),
    errors: null,
    ignoreDiscardDialog: false,
    isSubmitting: false
  }))

  const [showAllErrors, setShowAllErrors] = useState(false)
  const [, setForceRender] = useState(0)
  const inputValidatorsRef = useRef({})
  const pendingCallbacksRef = useRef([])
  const valueChangedRef = useRef(false)
  const initializedValidationRef = useRef(false)

  const forceUpdate = useCallback(() => {
    setForceRender(prev => prev + 1)
  }, [])

  const getInputValue = useCallback(inputPath => (
      getIn(formState, `value${inputPath}`)
  ), [formState])

  const getInputErrors = useCallback(inputPath => (
      getIn(formState, `errors${inputPath}`)
  ), [formState])

  const getValue = useCallback(() => ({
    ...(formState.value ?? {})
  }), [formState.value])

  const addValidators = useCallback((inputPath, validators) => {
    inputValidatorsRef.current[inputPath] = validators
  }, [])

  const removeValidators = useCallback(inputPath => {
    delete inputValidatorsRef.current[inputPath]
  }, [])

  const setInputErrors = useCallback((inputPath, errors) => {
    setFormState(prev => {
      const next = cloneDeep(prev)
      setIn(next, `errors${inputPath}`, errors)

      if (
          (prev.errors === null && next.errors === null) ||
          deepEqual(prev.errors, next.errors)
      ) {
        return prev
      }

      return next
    })
  }, [])

  const validateInput = useCallback(inputPath => {
    const validators = inputValidatorsRef.current[inputPath]

    if (!validators) {
      return []
    }

    return compact(
        validators.map(v => v(getInputValue(inputPath), formState.value))
    )
  }, [getInputValue, formState.value])

  const validate = useCallback((forceToShowAllErrors = true) => {
    let newErrors = { errors: {} }

    Object.keys(inputValidatorsRef.current).forEach(inputPath => {
      const inputErrors = validateInput(inputPath)
      newErrors = setIn(newErrors, `errors${inputPath}`, inputErrors)
    })

    setShowAllErrors(prev => prev || forceToShowAllErrors)

    const cleanedErrors = removeBlanksKeepArray(newErrors.errors)
    const normalizedErrors = cleanedErrors && !deepEqual(cleanedErrors, {})
        ? cleanedErrors
        : null

    setFormState(prev => {
      if (
          (normalizedErrors === null && prev.errors === null) ||
          (normalizedErrors !== null && deepEqual(prev.errors, normalizedErrors))
      ) {
        return prev
      }

      return {
        ...prev,
        errors: normalizedErrors
      }
    })

    return normalizedErrors === null
  }, [validateInput])

  const setInputValue = useCallback((path, value, callback) => {
    setFormState(prev => {
      if (Array.isArray(prev.value)) {
        throw new Error('Not implemented yet')
      }

      const trimmedPath = path.replace(/^\./, '')
      const nextValue = setIn(
          cloneDeep(prev.value ?? {}),
          trimmedPath,
          value
      )

      valueChangedRef.current = true

      if (callback) {
        pendingCallbacksRef.current.push(callback)
      }

      return {
        ...prev,
        value: nextValue
      }
    })
  }, [])

  const isValid = useCallback(() => formState.errors === null, [formState.errors])

  useEffect(() => {
    if (!initializedValidationRef.current) {
      initializedValidationRef.current = true
      validate(false)
    }
  }, [validate])

  useEffect(() => {
    setFormState(prev => {
      if (prev.value?.relationship) {
        return prev
      }

      return {
        ...prev,
        value: {
          ...prev.value,
          relationship: 'self'
        }
      }
    })
  }, [])

  useEffect(() => {
    if (!valueChangedRef.current) {
      return
    }

    valueChangedRef.current = false

    const newValue = formState.value ?? {}
    const initialValue = formState.initialValues ?? {}
    const newClean = removeBlanks(newValue)
    const initialClean = removeBlanks(initialValue)
    const isDirty = !deepEqual(newClean, initialClean)
    const { ignoreDiscardDialog } = formState

    if (!noGlobal) {
      if (
          isDirty &&
          !ignoreDiscardDialog &&
          typeof discardDialog?.onFormChange === 'function'
      ) {
        discardDialog.onFormChange(formKey)
        forceUpdate()
      }

      if (
          !isDirty &&
          typeof discardDialog?.resetForm === 'function'
      ) {
        discardDialog.resetForm(formKey)
        forceUpdate()
      }
    }

    onChange?.(newValue)

    if (pendingCallbacksRef.current.length) {
      pendingCallbacksRef.current.forEach(cb => cb?.())
      pendingCallbacksRef.current = []
    }

    validate()
  }, [
    formState,
    noGlobal,
    discardDialog,
    formKey,
    onChange,
    validate,
    forceUpdate
  ])

  const handleSubmit = useCallback(() => {
    setFormState(prev => ({
      ...prev,
      isSubmitting: true
    }))

    onSubmit?.(getValue())
  }, [onSubmit, getValue])

  const disableSave = !formState.value?.provider

  const form = useMemo(() => ({
    state: formState,
    showAllErrors,
    getValue,
    getInputValue,
    getInputErrors,
    setInputErrors,
    addValidators,
    removeValidators,
    validateInput,
    validate,
    setInputValue,
    isValid,
    forceUpdate
  }), [
    formState,
    showAllErrors,
    getValue,
    getInputValue,
    getInputErrors,
    setInputErrors,
    addValidators,
    removeValidators,
    validateInput,
    validate,
    setInputValue,
    isValid,
    forceUpdate
  ])


  const disabled = formState.value?.isSystemManaged && !canEditSystemManaged

  return (
      <LegacyFormProvider
          form={form}
          formKey={formKey}
      >
      <div className='margin-top margin-bottom'>
        <Paper>
          {formState.value?.isSystemManaged
            ? (
              <div className='row end-xs'>
                <SystemManaged />
              </div>
            )
            : null}
          <input
            type='hidden'
            name='id'
          />
          <input
            type='hidden'
            name='isSystemManaged'
          />
          <div className='row middle-xs'>
            <div className='col-xs-12 col-sm-6 col-md-4'>
              <TextInput
                label='Carrier Name'
                name='provider'
                disabled={disabled}
                required
              />
            </div>
            <div className='col-xs-12 col-sm-6 col-md-4'>
              <TextInput
                label='Plan'
                name='plan'
                disabled={disabled}
              />
            </div>
            <div className='col-xs-12 col-sm-6 col-md-4'>
              <TextInput
                label='Group #'
                name='groupNumber'
                disabled={disabled}
              />
            </div>
            <div className='col-xs-12 col-sm-6 col-md-4'>
              <TextInput
                label='Member Id'
                name='memberId'
                disabled={disabled}
              />
            </div>
            <div className='col-xs-12 col-sm-6 col-md-4'>
              <TextInput
                label='Subscriber Name'
                name='subscriberName'
                disabled={disabled}
              />
            </div>
            <div className='col-xs-12 col-sm-6 col-md-4'>
              <TextInput
                label='Subscriber ID'
                name='subscriberNumber'
                disabled={disabled}
              />
            </div>
            <div className='col-xs-12 col-sm-6 col-md-4'>
              <TextInput
                label='Dependent Id'
                name='dependentId'
                disabled={disabled}
              />
            </div>
            <div className='col-xs-12 col-sm-6 col-md-4'>
              <SelectInput
                label='Relationship'
                name='relationship'
                options={Relationships}
                disabled={disabled}
                required
              />
            </div>
            <div className='col-xs-12 col-sm-6 col-md-4 '>
              <MaskedTextInput
                name='phoneNumber'
                label='Phone Number'
                mask='111.111.1111'
                phone
              />
            </div>
            <div className='col-xs-12 col-sm-6'>
              <DateInput
                label='Eligibility Start'
                name='eligibilityStart'
                disabled={disabled}
              />
            </div>
            <div className='col-xs-12 col-sm-6'>
              <DateInput
                label='Eligibility End'
                name='eligibilityEnd'
                disabled={disabled}
              />
            </div>
            <div
              className='col-xs-offset-1 col-xs-11'
              style={{
                paddingTop: '15px',
                paddingBottom: '15px'
              }}
            >
              <RadioGroupInput
                name='primaryOrSecondary'
                options={[
                  {
                    label: 'Primary',
                    value: 'primary'
                  },
                  {
                    label: 'Secondary',
                    value: 'secondary'
                  }
                ]}
              />
            </div>
          </div>

          <div className='row margin-top end-xs'>
            <div className='col-xs-12'>
              <CancelButton
                noGlobal
                onClick={onCancel}
              />
              <SaveButton
                  disabled={disableSave || formState.isSubmitting}
                  onClick={handleSubmit}
              />
            </div>
          </div>
        </Paper>
      </div>
      </LegacyFormProvider>
  )
}

export default InsuranceForm
